import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  UserCircle,
  Calendar,
  TrendingUp,
  Mail,
  Phone,
  Users,
} from "lucide-react";
import api from "../../services/api";
import PageHeader from "../../components/ui/PageHeader";
import GlobalLoader from "../../components/ui/GlobalLoader";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";

export default function CustomersSummaryPage() {
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState<any[]>([]);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      // Fetch more to get good stats
      const response = await api.get(`/admin/customers?per_page=1000&page=1`);
      const data = response.data;
      let customerList: any[] = [];

      if (data.success && data.data && data.data.data) {
        customerList = data.data.data;
      } else if (data.data && Array.isArray(data.data)) {
        customerList = data.data;
      } else if (data.customers) {
        customerList = data.customers;
      }

      setCustomers(customerList || []);
    } catch (error) {
      console.error("Failed to fetch customers", error);
    } finally {
      setLoading(false);
    }
  };

  // --- Statistics & Derived Data ---
  const stats = useMemo(() => {
    const totalBookings = customers.reduce(
      (sum, c) => sum + (c.customer_bookings?.length || 0),
      0
    );
    const avgBookings =
      customers.length > 0
        ? (totalBookings / customers.length).toFixed(1)
        : "0.0";

    // Most Loyal (Max Bookings)
    const mostLoyal =
      customers.length > 0
        ? customers.reduce((prev, current) =>
            (prev.customer_bookings?.length || 0) >
            (current.customer_bookings?.length || 0)
              ? prev
              : current
          )
        : null;

    return { totalBookings, avgBookings, mostLoyal };
  }, [customers]);

  // Chart Data: Visits Distribution
  const visitDistributionData = useMemo(() => {
    const ranges = [
      { name: "1 Visit", min: 1, max: 1, count: 0, color: "#E9D5FF" },
      { name: "2-5 Visits", min: 2, max: 5, count: 0, color: "#C084FC" },
      { name: "6-10 Visits", min: 6, max: 10, count: 0, color: "#A855F7" },
      {
        name: "10+ Visits",
        min: 11,
        max: Infinity,
        count: 0,
        color: "#7E22CE",
      },
    ];

    let zeroVisits = 0;

    customers.forEach((c) => {
      const visits = c.customer_bookings?.length || 0;
      if (visits === 0) {
        zeroVisits++;
        return;
      }
      const range = ranges.find((r) => visits >= r.min && visits <= r.max);
      if (range) range.count++;
    });

    // Filter out zero count ranges to clean up chart if empty
    return ranges.filter((r) => r.count > 0);
  }, [customers]);

  // Chart Data: Top 5 Clients by Visits
  const topClientsData = useMemo(() => {
    return [...customers]
      .sort(
        (a, b) =>
          (b.customer_bookings?.length || 0) -
          (a.customer_bookings?.length || 0)
      )
      .slice(0, 5)
      .map((c) => ({
        name: c.name.split(" ")[0], // First name only for chart brevity
        fullName: c.name,
        visits: c.customer_bookings?.length || 0,
      }));
  }, [customers]);

  const topCustomersTable = useMemo(() => {
    return [...customers]
      .sort(
        (a, b) =>
          (b.customer_bookings?.length || 0) -
          (a.customer_bookings?.length || 0)
      )
      .slice(0, 5);
  }, [customers]);

  if (loading) {
    return (
      <div className="flex h-[80vh] items-center justify-center">
        <GlobalLoader description="Loading customers summary..." />
      </div>
    );
  }

  return (
    <div className="w-full">
      <PageHeader
        title="Customers Summary"
        description="Client database analytics and engagement metrics"
        icon={UserCircle}
        variant="default"
        className="mb-0"
      />

      <div className="space-y-6 pt-4">
        {/* Metric Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            label="Total Clients"
            value={customers.length}
            icon={Users}
            trend="+5 this week"
            trendUp={true}
          />
          <MetricCard
            label="Total Bookings"
            value={stats.totalBookings}
            icon={Calendar}
          />
          <MetricCard
            label="Avg. Visits / Client"
            value={stats.avgBookings}
            icon={TrendingUp}
          />
          <MetricCard
            label="Top Client Visits"
            value={stats.mostLoyal?.customer_bookings?.length || 0}
            subValue={stats.mostLoyal?.name || "-"}
            icon={UserCircle}
          />
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Top Clients Bar Chart */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h3 className="text-lg font-semibold text-[#402B69] mb-6">
              Top Clients by Visits
            </h3>
            <div className="h-[250px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={topClientsData}
                  margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    stroke="#F3F4F6"
                  />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#6B7280", fontSize: 12 }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#6B7280", fontSize: 12 }}
                  />
                  <RechartsTooltip
                    cursor={{ fill: "#F9FAFB" }}
                    contentStyle={{
                      borderRadius: "8px",
                      border: "none",
                      boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                    }}
                  />
                  <Bar
                    dataKey="visits"
                    fill="#885ABB"
                    radius={[4, 4, 0, 0]}
                    barSize={40}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Visit Frequency Pie Chart */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
            <h3 className="text-lg font-semibold text-[#402B69] mb-4">
              Visit Frequency
            </h3>
            <div className="h-[200px] w-full relative">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={visitDistributionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={85}
                    paddingAngle={0}
                    dataKey="count"
                    stroke="none"
                  >
                    {visitDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
              {/* Center Text */}
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-2xl font-bold text-gray-900">
                  {stats.totalBookings}
                </span>
                <span className="text-xs text-gray-500 font-medium">
                  Total Visits
                </span>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 mt-4">
              {visitDistributionData.map((item) => (
                <div key={item.name} className="flex items-center gap-1.5">
                  <div
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="text-xs text-gray-600">{item.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Top Active Clients Table */}
        <div className="bg-transparent lg:bg-white rounded-xl lg:border border-gray-200 lg:shadow-sm overflow-hidden px-4 lg:px-0">
          <div className="py-5 lg:p-5 border-b flex items-center justify-between bg-white lg:bg-transparent px-4 lg:px-5 rounded-t-xl lg:rounded-none border lg:border-none border-gray-100">
            <h3 className="text-base lg:text-lg font-bold lg:font-semibold text-[#402B69]">
              Top Active Clients
            </h3>
            <Link
              to="/customers"
              className="text-xs lg:text-sm text-[#885ABB] font-bold lg:font-medium hover:text-[#6f47a2] transition-colors"
            >
              View All
            </Link>
          </div>
          <div className="overflow-x-auto no-scrollbar">
            <table className="w-full text-sm text-left border-separate border-spacing-y-3 lg:border-spacing-y-0">
              <thead className="bg-[#885ABB] text-white font-medium hidden lg:table-header-group">
                <tr>
                  <th className="px-6 py-4 font-semibold uppercase tracking-wider text-xs">
                    Client Name
                  </th>
                  <th className="px-6 py-4 font-semibold uppercase tracking-wider text-xs">
                    Contact Info
                  </th>
                  <th className="px-6 py-4 font-semibold text-right uppercase tracking-wider text-xs">
                    Bookings
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-transparent lg:bg-white">
                {topCustomersTable.map((customer) => (
                  <tr
                    key={customer.id}
                    className="group bg-white lg:bg-transparent lg:hover:bg-gray-50/50 transition-colors flex flex-col lg:table-row rounded-xl shadow-sm lg:shadow-none border border-gray-100 lg:border-none p-4 lg:p-0"
                  >
                    <td className="lg:px-6 lg:py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 lg:h-8 lg:w-8 bg-purple-50 rounded-full flex items-center justify-center text-[#885ABB] font-bold text-xs lg:text-[10px] shrink-0 shadow-inner">
                          {customer.name?.charAt(0).toUpperCase() || "C"}
                        </div>
                        <span className="font-bold lg:font-medium text-gray-900 text-sm lg:text-base">
                          {customer.name}
                        </span>
                      </div>
                    </td>
                    <td className="lg:px-6 lg:py-4 text-gray-500 mt-3 lg:mt-0 pt-3 lg:pt-0 border-t lg:border-none border-gray-50">
                      <div className="flex flex-col gap-1.5 lg:gap-0.5">
                        {customer.email && (
                          <div className="flex items-center gap-2 lg:gap-1.5">
                            <Mail className="w-3.5 h-3.5 lg:w-3 lg:h-3 text-[#885ABB]/60" />
                            <span className="text-xs lg:text-[11px] font-medium lg:font-normal">
                              {customer.email}
                            </span>
                          </div>
                        )}
                        {customer.mobile && (
                          <div className="flex items-center gap-2 lg:gap-1.5">
                            <Phone className="w-3.5 h-3.5 lg:w-3 lg:h-3 text-[#885ABB]/60" />
                            <span className="text-xs lg:text-[11px] font-medium lg:font-normal">
                              {customer.mobile}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="lg:px-6 lg:py-4 lg:text-right mt-3 lg:mt-0 flex lg:table-cell justify-between items-center">
                      <span className="lg:hidden text-[10px] uppercase font-bold text-gray-400 tracking-wider">
                        Activity
                      </span>
                      <span className="inline-flex items-center px-3 py-1 lg:px-2.5 lg:py-0.5 rounded-full text-[10px] lg:text-xs font-bold lg:font-medium bg-purple-50 text-[#885ABB] border border-purple-100">
                        {customer.customer_bookings?.length || 0} visits
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({
  label,
  value,
  icon: Icon,
  subValue,
  trend,
  trendUp,
}: any) {
  return (
    <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-between h-full">
      <div className="flex items-start justify-between mb-4">
        <div className="p-2.5 bg-purple-50 rounded-lg">
          <Icon className="w-5 h-5 text-[#885ABB]" />
        </div>
        {trend && (
          <span
            className={`text-xs font-medium px-2 py-1 rounded-full ${
              trendUp ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600"
            }`}
          >
            {trend}
          </span>
        )}
      </div>
      <div>
        <h3 className="text-sm font-medium text-gray-500 mb-1">{label}</h3>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold text-gray-900">{value}</span>
          {subValue && (
            <span className="text-xs text-gray-400 truncate max-w-[120px]">
              {subValue}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
